/**
 * What to tell the learner when a reader could not read a page.
 *
 * A failed reader is not the learner's fault and not a finding about the work.
 * The one failure the learner can act on is `consent_required` - the reader was
 * refused because feedback consent is not granted - and that gets a link to the
 * screen where consent is reviewed. Every other kind gets a plain sentence from
 * `homework-labels.ts`, said once however many readers failed the same way.
 *
 * Takes only the part of a reader the sentence needs, so it serves both the
 * upload response (the page list) and the transcription response (the panel).
 */

import { Link } from 'react-router';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';

export interface ReaderOutcome {
  readonly outcome: 'succeeded' | 'failed';
  readonly failureKind?:
    | 'consent_required'
    | 'budget_refused'
    | 'api_key_missing'
    | 'paid_tier_unattested'
    | 'image_unreadable'
    | 'remote_call_failed'
    | 'response_blocked'
    | 'response_truncated'
    | 'empty_response'
    | 'invalid_output'
    | 'settings_echo_mismatch'
    | 'unclassified';
}

export function ReaderFailures({
  readers,
}: {
  readonly readers: readonly ReaderOutcome[];
}): React.JSX.Element | null {
  const t = useHomeworkLabels();
  const failed = readers.filter((reader) => reader.outcome === 'failed');
  if (failed.length === 0) return null;

  const kinds = [...new Set(failed.map((reader) => reader.failureKind ?? 'unclassified'))];
  return (
    <>
      {kinds.map((kind) =>
        kind === 'consent_required' ? (
          <p key={kind}>
            {t.readerConsentRefused} <Link to="/settings/consent">{t.reviewConsent}</Link>.
          </p>
        ) : (
          <p key={kind}>{t.readerFailure(kind)}</p>
        ),
      )}
    </>
  );
}
